import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { ButtonSpinner } from '../ui/Loader';
import {
  useGetProxy5gPackagesVisibilityQuery,
  useUpdateProxy5gPackagesBulkMutation,
  useUpdateProxy5gPackageVisibilityMutation,
  Proxy5gPackageItem,
} from '../../store/api/adminApi';

type DraftRow = {
  pkgId: number;
  defaultName: string;
  displayName: string;
  hiddenBot: boolean;
  hiddenWebsite: boolean;
};

function toDraft(packages: Proxy5gPackageItem[]): DraftRow[] {
  return [...packages]
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.id - b.id)
    .map((pkg) => ({
      pkgId: Number(pkg.id),
      defaultName: pkg.defaultName || pkg.name || `Package #${pkg.id}`,
      displayName: pkg.displayName || '',
      hiddenBot: !!pkg.hiddenBot,
      hiddenWebsite: !!pkg.hiddenWebsite,
    }));
}

/**
 * Merchant-facing Server 4 (5G) package manager: rename, reorder, hide bot/website.
 * Hide toggles save immediately; names/order save via button (or auto after reorder).
 */
export function Proxy5gPackagesManager() {
  const { data, isLoading, isFetching, refetch } = useGetProxy5gPackagesVisibilityQuery();
  const [saveBulk, { isLoading: savingBulk }] = useUpdateProxy5gPackagesBulkMutation();
  const [saveOne, { isLoading: savingOne }] = useUpdateProxy5gPackageVisibilityMutation();
  const [rows, setRows] = useState<DraftRow[]>([]);
  const [dirty, setDirty] = useState(false);
  const savingRef = useRef(false);

  useEffect(() => {
    if (data?.data?.packages) {
      setRows(toDraft(data.data.packages));
      setDirty(false);
    }
  }, [data]);

  const visibleCount = useMemo(
    () => rows.filter((r) => !r.hiddenBot || !r.hiddenWebsite).length,
    [rows]
  );

  const persistAll = async (nextRows: DraftRow[], { silent = false } = {}) => {
    if (savingRef.current) return;
    savingRef.current = true;
    try {
      await saveBulk({
        packages: nextRows.map((r, idx) => ({
          pkgId: r.pkgId,
          displayName: r.displayName.trim(),
          sortOrder: idx,
          hiddenBot: r.hiddenBot,
          hiddenWebsite: r.hiddenWebsite,
        })),
      }).unwrap();
      setDirty(false);
      if (!silent) toast.success('تم حفظ باقات بروكسي 4');
      await refetch();
    } catch (err) {
      const error = err as { data?: { message?: string }; status?: number };
      toast.error(
        error?.data?.message ||
          `فشل الحفظ${error?.status ? ` (${error.status})` : ''}`
      );
      throw err;
    } finally {
      savingRef.current = false;
    }
  };

  const move = async (index: number, dir: -1 | 1) => {
    const next = index + dir;
    if (next < 0 || next >= rows.length) return;
    const copy = [...rows];
    const tmp = copy[index];
    copy[index] = copy[next];
    copy[next] = tmp;
    setRows(copy);
    setDirty(true);
    try {
      await persistAll(copy, { silent: true });
      toast.success('تم تحديث الترتيب');
    } catch {
      /* toast already shown */
    }
  };

  const toggleHide = async (
    pkgId: number,
    field: 'hiddenBot' | 'hiddenWebsite'
  ) => {
    const current = rows.find((r) => r.pkgId === pkgId);
    if (!current) return;
    const nextVal = !current[field];
    setRows((prev) =>
      prev.map((r) => (r.pkgId === pkgId ? { ...r, [field]: nextVal } : r))
    );
    try {
      await saveOne({
        pkgId,
        [field]: nextVal,
      }).unwrap();
      toast.success(
        nextVal
          ? field === 'hiddenBot'
            ? 'تم إخفاء الباقة من البوت'
            : 'تم إخفاء الباقة من الموقع'
          : field === 'hiddenBot'
            ? 'تم إظهار الباقة في البوت'
            : 'تم إظهار الباقة في الموقع'
      );
      await refetch();
    } catch (err) {
      // revert
      setRows((prev) =>
        prev.map((r) => (r.pkgId === pkgId ? { ...r, [field]: !nextVal } : r))
      );
      const error = err as { data?: { message?: string }; status?: number };
      toast.error(
        error?.data?.message ||
          `فشل تحديث الإخفاء${error?.status ? ` (${error.status})` : ''}`
      );
    }
  };

  const patchName = (pkgId: number, displayName: string) => {
    setRows((prev) =>
      prev.map((r) => (r.pkgId === pkgId ? { ...r, displayName } : r))
    );
    setDirty(true);
  };

  const handleSave = async () => {
    try {
      await persistAll(rows);
    } catch {
      /* toast already shown */
    }
  };

  const handleResetNames = () => {
    setRows((prev) => prev.map((r) => ({ ...r, displayName: '' })));
    setDirty(true);
  };

  if (isLoading) {
    return (
      <div className="card-glass rounded-xl p-8 flex justify-center">
        <ButtonSpinner />
      </div>
    );
  }

  if (!rows.length) {
    return (
      <div className="card-glass rounded-xl p-6 mb-8">
        <h2 className="text-xl font-heading text-brand-accent mb-2">باقات بروكسي 4 (5G)</h2>
        <p className="text-sm text-neutral-medium">
          لا توجد باقات للعرض حالياً. تأكد أن خدمة البروكسي مفعّلة على خطتك.
        </p>
      </div>
    );
  }

  const busy = savingBulk || savingOne;

  return (
    <div className="card-glass rounded-xl p-6 mb-8">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-5">
        <div>
          <h2 className="text-xl font-heading text-brand-accent mb-1">باقات بروكسي 4 (5G)</h2>
          <p className="text-sm text-neutral-medium leading-relaxed max-w-2xl">
            الإخفاء يُحفظ فوراً. الأسماء والترتيب: عدّل ثم اضغط حفظ (الترتيب يُحفظ تلقائياً عند الأسهم).
          </p>
          <p className="text-xs text-neutral-medium mt-2">
            {rows.length} باقات · {visibleCount} ظاهرة في قناة واحدة على الأقل
            {isFetching ? ' · جاري التحديث…' : ''}
            {dirty ? ' · أسماء غير محفوظة' : ''}
          </p>
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          <button
            type="button"
            onClick={handleResetNames}
            disabled={busy}
            className="px-3 py-2 rounded-lg text-sm border border-neutral-dark/40 text-neutral-medium hover:text-neutral-light hover:border-neutral-medium transition-colors disabled:opacity-50"
          >
            مسح الأسماء المخصّصة
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={busy || !dirty}
            className="btn-primary px-5 py-2 rounded-lg font-body text-sm disabled:opacity-50"
          >
            {savingBulk ? 'جاري الحفظ…' : 'حفظ الأسماء'}
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {rows.map((row, index) => {
          const preview = row.displayName.trim() || row.defaultName;
          const fullyHidden = row.hiddenBot && row.hiddenWebsite;
          return (
            <div
              key={row.pkgId}
              className={`rounded-xl border p-4 transition-colors ${
                fullyHidden
                  ? 'border-red-500/20 bg-red-500/5 opacity-80'
                  : 'border-neutral-dark/30 bg-brand-primary/10'
              }`}
            >
              <div className="flex flex-col lg:flex-row gap-4 lg:items-center">
                <div className="flex items-center gap-2 lg:w-[7.5rem] shrink-0">
                  <div className="flex flex-col gap-1">
                    <button
                      type="button"
                      aria-label="تحريك للأعلى"
                      onClick={() => move(index, -1)}
                      disabled={busy || index === 0}
                      className="w-8 h-8 rounded-lg border border-neutral-dark/40 text-neutral-medium hover:text-brand-accent disabled:opacity-30"
                    >
                      ▲
                    </button>
                    <button
                      type="button"
                      aria-label="تحريك للأسفل"
                      onClick={() => move(index, 1)}
                      disabled={busy || index === rows.length - 1}
                      className="w-8 h-8 rounded-lg border border-neutral-dark/40 text-neutral-medium hover:text-brand-accent disabled:opacity-30"
                    >
                      ▼
                    </button>
                  </div>
                  <div className="text-center min-w-[2.5rem]">
                    <div className="text-lg font-heading text-brand-accent">{index + 1}</div>
                    <div className="text-[10px] text-neutral-medium">#{row.pkgId}</div>
                  </div>
                </div>

                <div className="flex-1 space-y-2 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-heading text-neutral-light truncate">{preview}</span>
                    {row.displayName.trim() && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-brand-accent/15 text-brand-accent">
                        مخصّص
                      </span>
                    )}
                    {fullyHidden && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/15 text-red-400">
                        مخفي بالكامل
                      </span>
                    )}
                  </div>
                  <input
                    value={row.displayName}
                    onChange={(e) => patchName(row.pkgId, e.target.value)}
                    placeholder={row.defaultName}
                    maxLength={80}
                    className="w-full bg-brand-primary/20 border border-neutral-dark/40 rounded-lg px-3 py-2 text-sm"
                  />
                  <p className="text-[11px] text-neutral-medium">الافتراضي: {row.defaultName}</p>
                </div>

                <div className="flex flex-col sm:flex-row gap-2 lg:w-72 shrink-0">
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => toggleHide(row.pkgId, 'hiddenBot')}
                    className={`flex-1 py-2.5 px-3 rounded-lg text-xs font-heading font-semibold border transition-colors disabled:opacity-50 ${
                      row.hiddenBot
                        ? 'bg-red-500/10 border-red-500/30 text-red-400'
                        : 'bg-green-500/10 border-green-500/30 text-green-400'
                    }`}
                  >
                    {row.hiddenBot ? 'مخفي في البوت' : 'ظاهر في البوت'}
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => toggleHide(row.pkgId, 'hiddenWebsite')}
                    className={`flex-1 py-2.5 px-3 rounded-lg text-xs font-heading font-semibold border transition-colors disabled:opacity-50 ${
                      row.hiddenWebsite
                        ? 'bg-red-500/10 border-red-500/30 text-red-400'
                        : 'bg-green-500/10 border-green-500/30 text-green-400'
                    }`}
                  >
                    {row.hiddenWebsite ? 'مخفي في الموقع' : 'ظاهر في الموقع'}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-5 flex justify-end">
        <button
          type="button"
          onClick={handleSave}
          disabled={busy || !dirty}
          className="btn-primary px-6 py-2.5 rounded-lg font-body disabled:opacity-50"
        >
          {savingBulk ? 'جاري الحفظ…' : 'حفظ الأسماء'}
        </button>
      </div>
    </div>
  );
}

export default Proxy5gPackagesManager;
